import PageContent from 'components/templates/PageContent'
import { Form, Formik } from 'formik'
import logo from 'assets/images/ajarlee.png'
import Input from 'components/atoms/Input'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import createForgotPasswordSchema from 'validations/forgot.validation'
import { useHistory, useLocation } from 'react-router'
import { useState, useEffect, useCallback } from 'react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'
import Modal from 'components/molecules/Modal'
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline'
import classNames from 'classnames'

const createResetPasswordSchema = t =>
  Yup.object().shape({
    newPassword: Yup.string().required(t('auth.forgotPassword.validation.newPasswordRequired')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], t('auth.forgotPassword.validation.passwordsMatch'))
      .required(t('auth.forgotPassword.validation.confirmPasswordRequired')),
  })

const FORGOT_PASSWORD_EMAIL_INITIAL_VALUES = { email: '' }
const FORGOT_PASSWORD_RESET_INITIAL_VALUES = { newPassword: '', confirmPassword: '' }

const ForgotPasswordEmailForm = ({ t, isRTL, onSubmit }) => (
  <>
    <h2 className={`mt-6 mb-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>{t('auth.forgotPassword.title')}</h2>
    <p className={`mt-11 text-center text-md text-lg-600 ${isRTL ? 'text-right' : 'text-left'}`}>{t('auth.forgotPassword.subtitle')}</p>
    <div className="mt-5 sm:mx-auto sm:w-full sm:max-w-md">
      <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <Formik validationSchema={createForgotPasswordSchema(t)} enableReinitialize initialValues={FORGOT_PASSWORD_EMAIL_INITIAL_VALUES} onSubmit={onSubmit}>
          {({ isSubmitting }) => (
            <Form className="mb-3">
              <div className="mb-3">
                <label htmlFor="email" className={`block text-sm text-normal text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('auth.forgotPassword.email')}
                </label>
                <div className="mt-1 text-gray-700">
                  <Input
                    id="email"
                    name="email"
                    placeholder={t('auth.forgotPassword.emailPlaceholder')}
                    type="email"
                    autoComplete="email"
                    required
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-dark-500 focus:border-dark-500 sm:text-sm ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  />
                </div>
              </div>
              <div>
                <button
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm text-normal text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.resetPassword')}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  </>
)

const ForgotPasswordResetForm = ({ t, isRTL, onSubmit }) => {
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  return (
    <>
      <h2 className={`mt-6 mb-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>{t('auth.forgotPassword.resetTitle')}</h2>
      <p className={`mt-11 text-center text-md text-lg-600 ${isRTL ? 'text-right' : 'text-left'}`}>{t('auth.forgotPassword.resetSubtitle')}</p>
      <div className="mt-5 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <Formik validationSchema={createResetPasswordSchema(t)} initialValues={FORGOT_PASSWORD_RESET_INITIAL_VALUES} onSubmit={onSubmit}>
            {({ isSubmitting, errors, touched, submitCount }) => (
              <Form className="mb-3">
                <div className="mb-3">
                  <label htmlFor="newPassword" className={`block text-sm text-normal text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('auth.forgotPassword.newPassword')}
                  </label>
                  <div className="mt-1 relative">
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-dashlane-ignore="true"
                      placeholder={t('auth.forgotPassword.newPasswordPlaceholder')}
                      className={`w-full ${
                        isRTL ? 'pr-10 pl-3' : 'pl-3 pr-10'
                      } py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-dark-500 focus:border-dark-500 sm:text-sm ${
                        isRTL ? 'text-right' : 'text-left'
                      }`}
                    />
                    {!(errors.newPassword && (touched.newPassword || submitCount > 0)) && (
                      <button
                        type="button"
                        className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center text-gray-400 hover:text-gray-600 focus:outline-none`}
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      >
                        {showNewPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="confirmPassword" className={`block text-sm text-normal text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('auth.forgotPassword.confirmPassword')}
                  </label>
                  <div className="mt-1 relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-dashlane-ignore="true"
                      placeholder={t('auth.forgotPassword.confirmPasswordPlaceholder')}
                      className={`w-full ${
                        isRTL ? 'pr-10 pl-3' : 'pl-3 pr-10'
                      } py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-dark-500 focus:border-dark-500 sm:text-sm ${
                        isRTL ? 'text-right' : 'text-left'
                      }`}
                    />
                    {!(errors.confirmPassword && (touched.confirmPassword || submitCount > 0)) && (
                      <button
                        type="button"
                        className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center text-gray-400 hover:text-gray-600 focus:outline-none`}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm text-normal text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? t('auth.forgotPassword.resettingPassword') : t('auth.forgotPassword.resetPassword')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  )
}

const ForgotPassword = () => {
  const history = useHistory()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const [currentStep, setCurrentStep] = useState('email')
  const [resetToken, setResetToken] = useState('')
  const [showEmailSentModal, setShowEmailSentModal] = useState(false)
  const [showInvalidLinkModal, setShowInvalidLinkModal] = useState(false)

  const isRTL = i18n.language === 'ar'

  const verifyResetLink = useCallback(
    async token => {
      try {
        const response = await apiPost('verify-reset-link', {
          resetToken: token,
        })

        if (response?.success) {
          notify.success(t('common.success'), t('auth.forgotPassword.messages.resetLinkValid'))
          setCurrentStep('reset')
        } else {
          // If response is not successful, show invalid link modal
          setShowInvalidLinkModal(true)
          setCurrentStep('email')
          setResetToken('')
          history.replace('/forgot')
        }
      } catch (error) {
        // Show modal instead of toast when link is invalid/expired
        setShowInvalidLinkModal(true)
        setCurrentStep('email')
        setResetToken('')
        // Clear the token from URL
        history.replace('/forgot')
      }
    },
    [t, history]
  )

  const handleResendLink = () => {
    setShowInvalidLinkModal(false)
    setCurrentStep('email')
    setResetToken('')
  }

  // Extract resetToken from URL query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const tokenFromUrl = searchParams.get('token')

    if (tokenFromUrl) {
      setResetToken(tokenFromUrl)
      verifyResetLink(tokenFromUrl)
    }
  }, [location.search, verifyResetLink])

  const sendPasswordForgetEmail = async data => {
    try {
      const response = await apiPost('forget-password', data)
      if (response?.success) {
        setShowEmailSentModal(true)
      }
    } catch (error) {
      notify.error(t('common.error'), t('auth.forgotPassword.messages.emailSendFailed'))
    }
  }

  const handleResetPassword = async (values, { setSubmitting }) => {
    try {
      const response = await apiPost('reset-password', {
        newPassword: values.newPassword,
        resetToken,
      })

      if (response?.success) {
        notify.success(t('common.success'), t('auth.forgotPassword.messages.passwordReset'))
        history.push('/login')
      }
    } catch (error) {
      notify.error(t('common.error'), error?.message || t('auth.forgotPassword.messages.passwordResetFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  const getCurrentForm = () => {
    switch (currentStep) {
      case 'email':
        return <ForgotPasswordEmailForm t={t} isRTL={isRTL} onSubmit={sendPasswordForgetEmail} />
      case 'reset':
        return <ForgotPasswordResetForm t={t} isRTL={isRTL} onSubmit={handleResetPassword} />
      default:
        return <ForgotPasswordEmailForm t={t} isRTL={isRTL} onSubmit={sendPasswordForgetEmail} />
    }
  }

  return (
    <PageContent center>
      <div className="bg-gray-50 flex flex-col min-h-screen justify-center py-12 sm:px-6 lg:px-8 relative">
        {/* Language Switcher */}
        <div className={classNames('absolute top-4 left-4 z-10', isRTL ? 'right-4' : 'left-4')}>
          <LanguageSwitcher compact />
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img className="mx-auto h-12 w-auto" src={logo} alt="Workflow" />
          {getCurrentForm()}
          <div className={`text-sm text-center mt-4 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('auth.forgotPassword.backToLogin')}{' '}
            <span onClick={() => history.push('/login')} className="cursor-pointer font-medium mt-3 text-primary-600 hover:text-primary-500">
              {t('auth.login.signIn')}
            </span>
          </div>
        </div>
      </div>

      {/* Email Sent Modal */}
      <Modal
        open={showEmailSentModal}
        onClose={() => setShowEmailSentModal(false)}
        closeable={true}
        title={t('auth.forgotPassword.emailSentModal.title')}
        width={500}
        height="auto"
        padding="p-6"
      >
        <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
          <p className="text-gray-700 mb-4">{t('auth.forgotPassword.emailSentModal.message')}</p>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => setShowEmailSentModal(false)}
              className="px-4 py-2 bg-dark-600 text-white rounded-md hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Invalid/Expired Link Modal */}
      <Modal
        open={showInvalidLinkModal}
        onClose={() => handleResendLink()}
        closeable={true}
        title={t('auth.forgotPassword.invalidLinkModal.title')}
        width={500}
        height="auto"
        padding="p-6"
      >
        <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
          <div className="mb-4">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <p className="text-gray-700 mb-2 text-center">{t('auth.forgotPassword.invalidLinkModal.message')}</p>
          </div>
          <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} justify-end gap-3 mt-6`}>
            <button
              onClick={() => handleResendLink()}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              {t('common.close')}
            </button>
            <button
              onClick={handleResendLink}
              className="px-4 py-2 bg-dark-600 text-white rounded-md hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
            >
              {t('auth.forgotPassword.invalidLinkModal.resendLink')}
            </button>
          </div>
        </div>
      </Modal>
    </PageContent>
  )
}

export default ForgotPassword
